import React, { FC, useMemo } from 'react'

import { graphql, useFragment } from 'react-relay'
import { useScript } from 'hoofd'

import { HOST_NAME } from '@data/config/url'

import { Metadata } from '../Metadata'
import { buildBreadcrumbList, HOME_BREADCRUMB } from '../buildBreadcrumbList'

import { GuildMetadata_guild$key } from './__generated__/GuildMetadata_guild.graphql'

export const GuildMetadata: FC<{
  guild: GuildMetadata_guild$key
  title?: string
  description?: string
}> = ({ guild, title, description }) => {
  const data = useFragment(
    graphql`
      fragment GuildMetadata_guild on Guild {
        slugId
        name
        description
        primaryPhoto {
          seoUrl: transformUrl(
            transform: { w: 500, h: 500, dpr: 1, fit: "contain" }
          )
        }
        backgroundPhoto {
          coverUrl: transformUrl(
            transform: { w: 1200, h: 600, dpr: 1, fit: "cover" }
          )
        }
        defaultCoverUrl: defaultBackgroundUrl(
          transform: { w: 1200, h: 600, dpr: 1, fit: "cover" }
        )
      }
    `,
    guild
  )

  const photoUrl =
    data.backgroundPhoto?.coverUrl || data.defaultCoverUrl

  const canonicalUrl = `${HOST_NAME}/${data.slugId}`

  const structuredData = useMemo(() => {
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: data.name,
      url: canonicalUrl,
      description: description || data.description,
      ...(data.primaryPhoto?.seoUrl
        ? { logo: data.primaryPhoto.seoUrl }
        : {}),
      ...(photoUrl ? { image: photoUrl } : {}),
    })
  }, [data, description, canonicalUrl, photoUrl])

  useScript({
    type: 'application/ld+json',
    text: structuredData,
  })

  const breadcrumbData = useMemo(
    () =>
      buildBreadcrumbList([
        HOME_BREADCRUMB,
        { name: data.name, url: canonicalUrl },
      ]),
    [data.name, canonicalUrl]
  )

  useScript({ type: 'application/ld+json', text: breadcrumbData })

  return (
    <Metadata
      canonicalUrl={canonicalUrl}
      title={title || data.name}
      description={description || data.description}
      imageTransformParams='NONE'
      imageWidth={1200}
      imageHeight={600}
      ogType="website"
      photoUrl={photoUrl}
    />
  )
}
